import type { Cabinet } from '../cabinets/model';
import type { HandleProduct } from '../doors/drilling';

/**
 * Prices a cabinet order from the made-to-measure tab. A port.
 *
 * <p>Only standard units are priced (owner, 2026-09-28): a cabinet that is
 * exactly a published standard unit costs that unit's price, its fronts are
 * priced by the server's door pricing, and anything modified is priced by the
 * shop on request. The live adapter (`cabinetOrders.ts`) rejects an order
 * holding a modified cabinet with {@link NeedsPriceRequest}. No price is ever
 * worked out in the browser.
 */
export interface CabinetOrderSpec {
  /** Door range and colour for the fronts; ignored for a carcass-only cabinet. */
  rangeId: string;
  colourId: string;
  /** The colour to paint, with `colourId` `ral` ("RAL 7016 Anthracite grey"). */
  paintColour?: string;
  handle: HandleProduct;
  cabinets: Cabinet[];
}

export interface CabinetQuoteLine {
  /** The standard unit this cabinet is sold as ("High line base unit, 600mm"). */
  standard?: string;
  carcass: number;
  fronts: number;
  hardware: number;
  /** One cabinet, all parts. */
  unit: number;
  /** `unit` × quantity. */
  total: number;
}

export interface CabinetQuote {
  currency: string;
  lines: CabinetQuoteLine[];
  cabinets: number;
  /** Goods total including VAT. Delivery is priced at checkout. */
  total: number;
}

export interface CabinetPricing {
  quote(spec: CabinetOrderSpec): Promise<CabinetQuote>;
}

/**
 * The order holds a cabinet that is not a standard unit left as it is, so it is
 * priced on request. `standard[i]` names cabinet i's standard unit, or is
 * undefined where cabinet i was modified.
 */
export class NeedsPriceRequest extends Error {
  /**
   * @param standard cabinet i's standard unit, or undefined where it was modified
   * @param reasons why a standard cabinet still cannot be priced online, where the shop said
   *   ("No standard door in Graphite is large enough for 2155 x 597 mm.")
   */
  constructor(
    readonly standard: ReadonlyArray<string | undefined>,
    readonly reasons: ReadonlyArray<string | undefined> = [],
  ) {
    super('Made-to-measure cabinets are priced on request.');
    this.name = 'NeedsPriceRequest';
  }
}
